import { Component, input, InputSignal, OnInit, output, OutputEmitterRef, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, takeUntil } from 'rxjs';
import {
    AutoFocusDirective,
    BaseComponent,
    ErrorsUtil,
    InputComponent,
    MessageBoxComponent,
    MessageContent,
    WaitButtonComponent
} from '@sircon/sircon-ui-framework';
import { AuthenticationService } from 'src/services/authentication.service';
import { MfaChallengeRequest, UserAuthenticationResponse } from 'src/app/core/models/login/user-authentication.model';
import { UserContext } from 'src/app/core/models/user/user-context.model';

export interface MfaChallengeInput {
    challengeName: string;
    session: string;
    userName: string;
}

@Component({
    selector: 'app-mfa-challenge',
    templateUrl: './mfa-challenge.component.html',
    styleUrls: ['./mfa-challenge.component.less'],
    imports: [
        ReactiveFormsModule,
        MessageBoxComponent,
        InputComponent,
        WaitButtonComponent,
        AutoFocusDirective
    ],
    standalone: true
})
export class MfaChallengeComponent extends BaseComponent implements OnInit {
    challengeInput: InputSignal<MfaChallengeInput> = input.required<MfaChallengeInput>();
    verified: OutputEmitterRef<UserAuthenticationResponse | UserContext> = output<UserAuthenticationResponse | UserContext>();

    verificationForm: FormGroup;
    errors: MessageContent;
    verifying: WritableSignal<boolean> = signal(false);

    constructor(
        private formBuilder: FormBuilder,
        private authenticationService: AuthenticationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.errors = ErrorsUtil.create();

        this.verificationForm = this.formBuilder.group({
            verificationCode: ['', Validators.compose([
                Validators.required,
                Validators.minLength(6),
                Validators.maxLength(6),
                Validators.pattern(/^[0-9]+$/)
            ])]
        });
    }

    verifyCode(): void {
        if (!this.verificationForm.valid) {
            this.verificationForm.markAllAsTouched();
            return;
        }

        this.verifying.set(true);
        ErrorsUtil.clear(this.errors);

        const request: MfaChallengeRequest = {
            challengeName: this.challengeInput().challengeName,
            session: this.challengeInput().session,
            userName: this.challengeInput().userName,
            code: this.verificationForm.value.verificationCode
        };

        this.authenticationService.verifyMfaChallengeAsObservable(request)
            .pipe(
                takeUntil(this.ngUnsubscribe),
                finalize(() => this.verifying.set(false))
            )
            .subscribe({
                next: (response: UserAuthenticationResponse | UserContext) => {
                    this.verified.emit(response);
                },
                error: (error) => {
                    this.errors = ErrorsUtil.mapResponse(error);
                }
            });
    }
}
